/**
 * Chat Loading Skeleton
 *
 * Beautiful loading state for chat messages being fetched
 * Shows animated skeleton bubbles and pulse effects
 */

import React from 'react';

export const ChatLoadingSkeleton: React.FC = () => {
  return (
    <div className="flex flex-col h-full items-center justify-center p-6 space-y-6 animate-in fade-in duration-300">
      {/* Spinner with modern design */}
      <div className="relative">
        <div className="w-16 h-16 rounded-full border-4 border-primary/20 border-t-primary animate-spin" />
        <div
          className="absolute inset-0 w-16 h-16 rounded-full border-4 border-transparent border-r-primary/40 animate-spin"
          style={{ animationDirection: 'reverse', animationDuration: '1s' }}
        />
      </div>

      {/* Loading text */}
      <div className="text-center space-y-2">
        <div className="text-sm font-medium text-foreground">Loading conversation...</div>
        <div className="text-xs text-muted-foreground">Fetching message history</div>
      </div>

      {/* Message skeleton bubbles */}
      <div className="w-full max-w-2xl space-y-4 mt-8">
        {/* User message skeleton */}
        <div className="flex justify-end animate-pulse">
          <div className="w-3/4 space-y-2">
            <div className="h-16 bg-primary/10 rounded-2xl rounded-tr-sm" />
          </div>
        </div>

        {/* AI message skeleton */}
        <div className="flex justify-start animate-pulse" style={{ animationDelay: '150ms' }}>
          <div className="w-4/5 space-y-2">
            <div className="h-24 bg-muted rounded-2xl rounded-tl-sm" />
          </div>
        </div>

        {/* User message skeleton */}
        <div className="flex justify-end animate-pulse" style={{ animationDelay: '300ms' }}>
          <div className="w-2/3 space-y-2">
            <div className="h-12 bg-primary/10 rounded-2xl rounded-tr-sm" />
          </div>
        </div>
      </div>

      {/* Progress dots */}
      <div className="flex gap-2 mt-4">
        <div className="w-2 h-2 rounded-full bg-primary animate-bounce" />
        <div
          className="w-2 h-2 rounded-full bg-primary animate-bounce"
          style={{ animationDelay: '150ms' }}
        />
        <div
          className="w-2 h-2 rounded-full bg-primary animate-bounce"
          style={{ animationDelay: '300ms' }}
        />
      </div>
    </div>
  );
};

/**
 * Minimal loading indicator for inline use
 */
export const ChatLoadingSpinner: React.FC<{ message?: string }> = ({ message = 'Loading...' }) => {
  return (
    <div className="flex items-center justify-center gap-3 py-8">
      <div className="w-6 h-6 rounded-full border-2 border-primary/20 border-t-primary animate-spin" />
      <span className="text-sm text-muted-foreground">{message}</span>
    </div>
  );
};
